// lib/studio-layer-tiles.ts

// One canvas near the 16384px GPU texture limit (a 16383x7897 upscale) stalled the browser ~100 ms every few frames
// while panning, erratically and at mid zooms only; the same pixels split into 4096px canvases held 60 fps (measured).
// So 4096 stays the bitmap side: the stride is what shrinks, and what it gives up is redrawn as bleed on the
// right and bottom edges. Under a fractional effective scale Chrome lays each sibling canvas out on its own, and at
// some zooms neither tile covered the device pixel of the seam, letting the workspace background through. Tiles are
// emitted row-major and carry no z-index, so the right and bottom neighbours always paint over the bleed: bleeding
// left or top would be painted over, hence paid for nothing.
// The bleed is counted in source pixels but the seam is one device pixel wide, so shrinking the layer on screen
// shrinks the cover: 2px still showed the seam at zoom 0.8 (observed). 8px clears it everywhere we zoom, bar a
// rare 1px seam on the very largest layers at heavy zoom-out, accepted rather than paid for with a wider bleed:
// the overlap composes twice, so on a partly transparent layer it is itself a darker line.
export const TILE_ABOVE_SIDE_PX = 8192;
const TILE_SIDE_PX = 4096;
export const TILE_BLEED_PX = 8;
const TILE_STRIDE_PX = TILE_SIDE_PX - TILE_BLEED_PX;

export type RasterTile = { x: number; y: number; w: number; h: number };

const resolveTileOffsets = (size: number): number[] => {
    const offsets: number[] = [];
    for (let offset = 0; offset < size; offset += TILE_STRIDE_PX) {
        // What is left here is already covered by the bleed of the previous tile, which then ends exactly on the
        // edge: emitting this one would only add a canvas whose pixels compose twice on a translucent layer.
        if (offset > 0 && size - offset <= TILE_BLEED_PX) break;
        offsets.push(offset);
    }
    return offsets;
};

export const resolveRasterTiles = (width: number, height: number): RasterTile[] => {
    const tiles: RasterTile[] = [];
    for (const y of resolveTileOffsets(height)) {
        for (const x of resolveTileOffsets(width)) {
            tiles.push({ x, y, w: Math.min(TILE_SIDE_PX, width - x), h: Math.min(TILE_SIDE_PX, height - y) });
        }
    }
    return tiles;
};
